import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import axios from 'axios';
import {
  FiSend,
  FiImage,
  FiX,
  FiSquare,
  FiPlus,
  FiMic,
  FiMicOff,
  FiFileText
} from 'react-icons/fi';
import {
  ThumbsUp,
  ThumbsDown,
  Share,
  Copy,
  Check,
  Volume2,
  VolumeX,
  Loader2,
  RotateCw,
  ArrowDown
} from 'lucide-react';
import { RiVoiceprintFill } from 'react-icons/ri';
import { toast } from 'sonner';
import { SerafinaVoiceMode } from '../components/SerafinaVoiceMode';
import { MarkdownMessage } from '../components/MarkdownMessage';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider
} from '../components/ui/tooltip';

const SERAFINA_AVATAR = '/images/serafina.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_GREETING = {
  role: 'assistant',
  content: 'Olá! Sou a Serafina, sua assistente financeira. Posso te ajudar com dúvidas sobre suas finanças, analisar planilhas e extratos, criar registros e metas, ler comprovantes bancários e muito mais. Como posso ajudá-lo hoje?'
};

export const Assistente = () => {
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = Boolean(user?.email && ADMIN_EMAILS.includes(user.email));
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date());

  const [messages, setMessages] = useState(() => {
    const saved = sessionStorage.getItem('serafina_messages');
    return saved ? JSON.parse(saved) : [DEFAULT_GREETING];
  });
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState([]); // [{ id, base64, name }]
  const [pendingFiles, setPendingFiles] = useState([]); // [{ id, base64, name, size, type }]
  const [expandedImage, setExpandedImage] = useState(null); // base64 string for lightbox
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isVoiceModeActive, setIsVoiceModeActive] = useState(false);

  // Voice recording & playback states
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [playingAudioIndex, setPlayingAudioIndex] = useState(null);
  const [loadingAudioIndex, setLoadingAudioIndex] = useState(null);
  const [feedbackMap, setFeedbackMap] = useState({}); // { [messageIndex]: 'like' | 'dislike' }
  const [copiedIndex, setCopiedIndex] = useState(null);

  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const attachMenuRef = useRef(null);
  const abortControllerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight, offsetWidth, clientWidth } = scrollContainerRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    setShowScrollBottom(distanceFromBottom > 120);

    const sw = Math.max(0, offsetWidth - clientWidth);
    if (sw !== scrollbarWidth) {
      setScrollbarWidth(sw);
    }
  };

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Sync scrollbar width with bottom bar to guarantee pixel-perfect centering
  useEffect(() => {
    const updateScrollbar = () => {
      if (scrollContainerRef.current) {
        const sw = Math.max(0, scrollContainerRef.current.offsetWidth - scrollContainerRef.current.clientWidth);
        setScrollbarWidth(sw);
      }
    };
    updateScrollbar();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && scrollContainerRef.current) {
      ro = new ResizeObserver(updateScrollbar);
      ro.observe(scrollContainerRef.current);
    }
    window.addEventListener('resize', updateScrollbar);
    return () => {
      window.removeEventListener('resize', updateScrollbar);
      ro?.disconnect();
    };
  }, [messages]);

  useEffect(() => {
    if (!showScrollBottom) {
      scrollToBottom();
    }
  }, [messages, showScrollBottom]);

  useEffect(() => {
    sessionStorage.setItem('serafina_messages', JSON.stringify(messages));
  }, [messages]);

  // Close lightbox on Escape and menu on outside click
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setExpandedImage(null);
        setShowAttachMenu(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    };
    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachMenu]);

  // Ensure form is scrolled into view when pending attachments are added
  useEffect(() => {
    if (pendingImages.length > 0 || pendingFiles.length > 0) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 60);
    }
  }, [pendingImages.length, pendingFiles.length]);

  // Handle incoming files (images or documents)
  const addFilesToList = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    const images = files.filter(f => f.type && f.type.startsWith('image/'));
    const documents = files.filter(f => !f.type || !f.type.startsWith('image/'));

    if (images.length > 0) {
      images.forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPendingImages(prev => [
            ...prev,
            {
              id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              base64: event.target.result,
              name: file.name || `imagem_${Date.now()}.png`
            }
          ]);
        };
        reader.readAsDataURL(file);
      });
      toast.success(images.length === 1 ? 'Imagem anexada!' : `${images.length} imagens anexadas!`);
    }

    if (documents.length > 0) {
      documents.forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPendingFiles(prev => [
            ...prev,
            {
              id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: file.name || `arquivo_${Date.now()}`,
              size: file.size,
              type: file.type || 'application/octet-stream',
              base64: event.target.result
            }
          ]);
        };
        reader.readAsDataURL(file);
      });
      toast.success(documents.length === 1 ? `Arquivo "${documents[0].name}" anexado!` : `${documents.length} arquivos anexados!`);
    }
  };

  // Unified Ctrl+V paste handler for images and files
  const handlePasteEvent = (e) => {
    const clipboardFiles = e.clipboardData?.files;
    if (clipboardFiles && clipboardFiles.length > 0) {
      e.preventDefault();
      addFilesToList(clipboardFiles);
      return;
    }

    const clipboardItems = e.clipboardData?.items;
    if (!clipboardItems) return;

    const filesFound = [];
    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) filesFound.push(file);
      }
    }

    if (filesFound.length > 0) {
      e.preventDefault();
      addFilesToList(filesFound);
    }
  };

  // Allow global Ctrl+V anywhere on Assistente page
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      handlePasteEvent(e);
    };
    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, []);

  // Voice playback management (edge-tts)
  const stopSpeech = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setPlayingAudioIndex(null);
  };

  const playSpeech = async (text, messageIndex) => {
    if (playingAudioIndex === messageIndex) {
      stopSpeech();
      return;
    }

    stopSpeech();
    setLoadingAudioIndex(messageIndex);

    try {
      const response = await axios.post(
        `${API}/ai/tts`,
        { text, voice: 'pt-BR-FranciscaNeural' },
        { responseType: 'blob', withCredentials: true }
      );

      const audioBlob = response.data;
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      setPlayingAudioIndex(messageIndex);

      audio.onended = () => {
        setPlayingAudioIndex(null);
        currentAudioRef.current = null;
      };
      audio.onerror = (err) => {
        console.error('Audio playback error:', err);
        setPlayingAudioIndex(null);
        currentAudioRef.current = null;
        toast.error('Erro ao reproduzir o áudio da Serafina.');
      };

      await audio.play();
    } catch (error) {
      console.error('Error fetching voice audio:', error);
      toast.error('Não foi possível gerar a voz da Serafina no momento.');
    } finally {
      setLoadingAudioIndex(null);
    }
  };

  // Helper to retrieve the user's prompt corresponding to an assistant response
  const getPromptForMessage = (messageIndex) => {
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        return messages[i].content || '';
      }
    }
    return '';
  };

  // Like & Dislike feedback handler
  const handleFeedback = async (messageIndex, type) => {
    const current = feedbackMap[messageIndex];
    const nextType = current === type ? null : type;

    setFeedbackMap(prev => ({ ...prev, [messageIndex]: nextType }));

    if (!nextType) return; // User unclicked

    try {
      const prompt = getPromptForMessage(messageIndex);
      const messageContent = messages[messageIndex]?.content || '';
      await axios.post(
        `${API}/ai/feedback`,
        {
          message_content: messageContent,
          prompt,
          feedback: nextType
        },
        { withCredentials: true }
      );
      if (nextType === 'like') {
        toast.success('Obrigado pelo feedback!');
      } else {
        toast.info('Feedback registrado. Vamos trabalhar para melhorar.');
      }
    } catch (err) {
      console.error('Error recording AI feedback:', err);
    }
  };

  // Copy message text handler
  const handleCopyMessage = async (content, messageIndex) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(messageIndex);
      toast.success('Mensagem copiada!');
      setTimeout(() => {
        setCopiedIndex(prev => (prev === messageIndex ? null : prev));
      }, 2000);
    } catch (err) {
      console.error('Error copying message:', err);
      toast.error('Erro ao copiar a mensagem.');
    }
  };

  // Share message & prompt handler
  const handleShareMessage = async (content, messageIndex) => {
    const prompt = getPromptForMessage(messageIndex);
    const shareText = prompt
      ? `*Pergunta:* ${prompt}\n\n*Resposta da Serafina:*\n${content}`
      : content;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Serafina IA - Finanças',
          text: shareText
        });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      toast.success('Pergunta e resposta copiadas para compartilhamento!');
    } catch (err) {
      console.error('Error sharing message:', err);
      toast.error('Não foi possível compartilhar.');
    }
  };

  // Regenerate assistant response handler
  const handleRegenerate = async (messageIndex) => {
    if (loading || isStreaming) return;
    stopSpeech();

    // Identify the user prompt corresponding to this assistant turn
    let userMsgIndex = -1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        userMsgIndex = i;
        break;
      }
    }

    if (userMsgIndex === -1) {
      toast.error('Não foi possível identificar a pergunta anterior.');
      return;
    }

    const userMessage = messages[userMsgIndex];
    const previousHistory = messages
      .slice(0, userMsgIndex)
      .filter(m => !m.error)
      .map(m => ({ role: m.role, content: m.content }));

    // Reset this assistant message to blank and trigger generation
    setMessages(prev => {
      const next = [...prev];
      next[messageIndex] = { role: 'assistant', content: '' };
      return next;
    });

    setLoading(true);
    setIsStreaming(false);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const payload = {
        message: userMessage.content,
        chat_history: previousHistory,
        image_base64: userMessage.image || null,
        images_base64: userMessage.images || [],
        files: []
      };

      let accumulatedText = '';
      let streamSucceeded = false;
      let hasReceivedFirstChunk = false;
      let finalCleanResponse = null;

      try {
        const token = localStorage.getItem('finance_session_token');
        const streamHeaders = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const streamResponse = await fetch(`${API}/ai/chat/stream`, {
          method: 'POST',
          headers: streamHeaders,
          body: JSON.stringify(payload),
          credentials: 'include',
          signal: controller.signal
        });

        if (streamResponse.ok && streamResponse.body) {
          const reader = streamResponse.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.slice(5).trim();
              if (!jsonStr) continue;

              try {
                const data = JSON.parse(jsonStr);
                if (data.chunk) {
                  accumulatedText += data.chunk;
                  if (!hasReceivedFirstChunk) {
                    hasReceivedFirstChunk = true;
                    setLoading(false);
                    setIsStreaming(true);
                  }
                  const currentText = accumulatedText;
                  setMessages(prev => {
                    const next = [...prev];
                    next[messageIndex] = { role: 'assistant', content: currentText };
                    return next;
                  });
                }
                if (data.done) {
                  if (data.clean_response) finalCleanResponse = data.clean_response;
                }
              } catch (e) {}
            }
          }
          streamSucceeded = hasReceivedFirstChunk || Boolean(accumulatedText);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }

      if (!streamSucceeded) {
        const response = await axios.post(`${API}/ai/chat`, payload, {
          withCredentials: true,
          signal: controller.signal
        });
        finalCleanResponse = response.data?.response || '';
      }

      const finalText = (finalCleanResponse || accumulatedText || 'Desculpe, ocorreu um erro ao regenerar a resposta.').trim();
      setMessages(prev => {
        const next = [...prev];
        next[messageIndex] = { role: 'assistant', content: finalText };
        return next;
      });
      toast.success('Resposta gerada novamente!');
    } catch (e) {
      if (e.name === 'AbortError' || axios.isCancel(e)) return;
      console.error('Error regenerating response:', e);
      toast.error('Erro ao gerar novamente.');
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
      setIsStreaming(false);
    }
  };

  // Voice recording (Microphone via MediaRecorder for 100% browser compatibility)
  const startRecording = async () => {
    try {
      stopSpeech();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm'
        });
        stream.getTracks().forEach(track => track.stop());

        if (audioBlob.size > 0) {
          await transcribeAudioBlob(audioBlob);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Gravando... Pode falar sua dúvida ou instrução!');
    } catch (err) {
      console.error('Microphone error:', err);
      toast.error('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const transcribeAudioBlob = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = reader.result;
        const resp = await axios.post(
          `${API}/ai/transcribe`,
          {
            audio_base64: base64Audio,
            mime_type: audioBlob.type || 'audio/webm'
          },
          { withCredentials: true }
        );

        const text = resp.data?.text;
        if (text && text.trim()) {
          setInput(prev => (prev ? `${prev} ${text.trim()}` : text.trim()));
          toast.success('Áudio transcrito com sucesso!');
        } else {
          toast.warning('Nenhuma fala nítida foi detectada. Tente falar novamente.');
        }
        setIsTranscribing(false);
      };
      reader.readAsDataURL(audioBlob);
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error('Erro ao transcrever o áudio gravado.');
      setIsTranscribing(false);
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    stopSpeech();
    setLoading(false);
    setIsStreaming(false);
    toast.info('Geração interrompida.');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const hasImages = pendingImages.length > 0;
    const hasFiles = pendingFiles.length > 0;
    if ((!input.trim() && !hasImages && !hasFiles) || loading || isStreaming) return;

    stopSpeech();
    if (isRecording) stopRecording();

    const userText = input.trim();
    let displayContent;
    const parts = [];
    if (hasImages) {
      parts.push(pendingImages.length === 1 ? '[Comprovante anexado]' : `[${pendingImages.length} Comprovantes anexados]`);
    }
    if (hasFiles) {
      parts.push(pendingFiles.length === 1 ? `[Arquivo: ${pendingFiles[0].name}]` : `[${pendingFiles.length} Arquivos anexados]`);
    }
    if (userText) {
      displayContent = parts.length > 0 ? `${parts.join(' ')}\n${userText}` : userText;
    } else {
      displayContent = parts.join(' ');
    }

    const currentImages = [...pendingImages];
    const currentFiles = [...pendingFiles];
    const userMessage = {
      role: 'user',
      content: displayContent,
      image: hasImages ? currentImages[0].base64 : undefined,
      images: hasImages ? currentImages.map(img => img.base64) : undefined,
      fileNames: hasFiles ? currentFiles.map(f => f.name) : undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setPendingImages([]);
    setPendingFiles([]);
    setLoading(true);
    setIsStreaming(false);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let promptText = userText;
      if (hasImages && hasFiles) {
        promptText = userText
          ? `${userText}\n\n(O usuário anexou ${currentImages.length} imagens e ${currentFiles.length} arquivos/planilhas para análise financeira)`
          : `Analise as imagens e os arquivos anexados, extraia as despesas e receitas e ofereça resumo e criação de registros.`;
      } else if (hasImages) {
        const count = currentImages.length;
        const countStr = count === 1 ? 'um comprovante bancário' : `${count} comprovantes bancários`;
        promptText = userText
          ? `O usuário enviou ${countStr} (anexo) com a mensagem: "${userText}". Analise as imagens dos comprovantes, extraia valor, descrição, data e tipo (pagar/receber) de cada um e crie os registros automaticamente. Se algum dado estiver ilegível, peça apenas o que falta.`
          : `O usuário enviou ${countStr} (anexo). Analise as imagens dos comprovantes, extraia valor, descrição, data e tipo (pagar/receber) de cada um e crie os registros automaticamente. Se algum dado estiver ilegível, peça apenas o que falta.`;
      } else if (hasFiles) {
        promptText = userText
          ? `${userText}\n\n(Consulte a tabela/planilha/arquivo anexado para responder e analisar com detalhes financeiros)`
          : `Analise as tabelas e dados financeiros contidos nos arquivos anexados. Forneça um resumo claro e pergunte se o usuário deseja registrar alguma despesa ou meta.`;
      }

      const payload = {
        message: promptText,
        chat_history: messages.filter(m => !m.error).map(m => ({ role: m.role, content: m.content })),
        image_base64: hasImages ? currentImages[0].base64 : null,
        images_base64: hasImages ? currentImages.map(img => img.base64) : [],
        files: currentFiles.map(f => ({
          name: f.name,
          content: f.base64,
          type: f.type
        }))
      };

      let streamSucceeded = false;
      let streamHadError = false;
      let accumulatedText = '';
      let hasReceivedFirstChunk = false;
      let finalCleanResponse = null;
      let createdItems = [];

      try {
        const token = localStorage.getItem('finance_session_token');
        const streamHeaders = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const streamResponse = await fetch(`${API}/ai/chat/stream`, {
          method: 'POST',
          headers: streamHeaders,
          body: JSON.stringify(payload),
          credentials: 'include',
          signal: controller.signal
        });

        if (streamResponse.ok && streamResponse.body) {
          const reader = streamResponse.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.slice(5).trim();
              if (!jsonStr) continue;

              try {
                const data = JSON.parse(jsonStr);

                if (data.chunk) {
                  accumulatedText += data.chunk;

                  if (!hasReceivedFirstChunk) {
                    hasReceivedFirstChunk = true;
                    setLoading(false);
                    setIsStreaming(true);
                    setMessages(prev => [...prev, { role: 'assistant', content: accumulatedText }]);
                  } else {
                    setMessages(prev => {
                      const next = [...prev];
                      if (next.length > 0 && next[next.length - 1].role === 'assistant') {
                        next[next.length - 1] = {
                          ...next[next.length - 1],
                          content: accumulatedText
                        };
                      }
                      return next;
                    });
                  }
                }

                if (data.done) {
                  if (data.clean_response) finalCleanResponse = data.clean_response;
                  if (data.created_items) createdItems = data.created_items;
                  if (data.error) streamHadError = true;
                }
              } catch (parseErr) {
                // Ignore partial JSON frame errors
              }
            }
          }

          streamSucceeded = !streamHadError && (hasReceivedFirstChunk || Boolean(accumulatedText));
        }
      } catch (streamErr) {
        if (streamErr.name === 'AbortError' || axios.isCancel(streamErr)) {
          return;
        }
        console.warn('Live stream connection failed, falling back to standard /ai/chat:', streamErr);
      }

      // Fallback to standard /ai/chat if streaming was unavailable or failed
      if (!streamSucceeded) {
        try {
          const response = await axios.post(`${API}/ai/chat`, payload, {
            withCredentials: true,
            signal: controller.signal
          });

          const rawResponse = response.data?.response || 'Desculpe, não consegui processar sua mensagem.';
          finalCleanResponse = rawResponse;
          createdItems = response.data?.created_items || [];
          streamSucceeded = true;

          // Progressive word-by-word reveal for fallback to reduce perceived wait time
          setLoading(false);
          setIsStreaming(true);
          const words = rawResponse.split(' ');
          accumulatedText = '';

          setMessages(prev => {
            const next = [...prev];
            if (next.length > 0 && next[next.length - 1].role === 'assistant') {
              next[next.length - 1] = { ...next[next.length - 1], content: '' };
              return next;
            } else {
              return [...prev, { role: 'assistant', content: '' }];
            }
          });

          for (let i = 0; i < words.length; i++) {
            if (controller.signal.aborted) return;
            accumulatedText += (i === 0 ? '' : ' ') + words[i];
            const currentText = accumulatedText;
            setMessages(prev => {
              const next = [...prev];
              if (next.length > 0 && next[next.length - 1].role === 'assistant') {
                next[next.length - 1] = { ...next[next.length - 1], content: currentText };
              }
              return next;
            });
            await new Promise(r => setTimeout(r, 15));
          }
        } catch (fallbackErr) {
          if (fallbackErr.name === 'AbortError' || axios.isCancel(fallbackErr)) return;
          console.error('Fallback /ai/chat error:', fallbackErr);
        }
      }

      const finalText = (finalCleanResponse || accumulatedText || 'Desculpe, estou com dificuldades temporárias para responder no momento. Por favor, tente novamente em instantes.').trim();

      if (finalText.includes('[REFAZER_TOUR]')) {
        localStorage.removeItem('finance_tour_done');
        window.location.href = '/dashboard';
        return;
      }

      // Guarantee the assistant message bubble is ALWAYS populated, never left blank
      setMessages(prev => {
        const next = [...prev];
        if (next.length > 0 && next[next.length - 1].role === 'assistant') {
          next[next.length - 1] = { ...next[next.length - 1], content: finalText };
          return next;
        } else {
          return [...prev, { role: 'assistant', content: finalText }];
        }
      });


      if (createdItems && createdItems.length > 0) {
        createdItems.forEach(item => {
          if (item.type === 'registro') {
            toast.success(`Registro "${item.description}" de R$ ${item.value.toFixed(2)} criado!`);
          } else if (item.type === 'meta') {
            toast.success(`Meta "${item.title}" de R$ ${item.target_amount.toFixed(2)} criada!`);
          }
        });
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error sending message:', error);
      const errorMsg = 'Desculpe, ocorreu uma instabilidade momentânea ao processar sua mensagem. Por favor, tente novamente em instantes.';
      setMessages(prev => {
        const next = [...prev];
        if (next.length > 0 && next[next.length - 1].role === 'assistant') {
          next[next.length - 1] = { ...next[next.length - 1], content: errorMsg, error: true };
          return next;
        } else {
          return [...prev, { role: 'assistant', content: errorMsg, error: true }];
        }
      });
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
      setIsStreaming(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Serafina IA" />
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen max-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors flex flex-col overflow-hidden">
      <Header />

      <main className="transition-all duration-300 flex-1 flex flex-col min-h-0 relative">
        {/* Full-width scroll container - puts scrollbar at far right of browser window */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="w-full flex-1 overflow-y-auto min-h-0"
        >
          {/* Centered chat content column matching input width */}
          <div className="max-w-3xl sm:max-w-4xl w-full mx-auto px-4 sm:px-6 py-4 flex flex-col min-h-full pb-32 sm:pb-36">
            
            {/* Header Bar with Serafina info */}
            <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b border-slate-200/60 dark:border-white/5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <img src={SERAFINA_AVATAR} alt="Serafina" className="w-10 h-10 rounded-full object-cover shadow-sm ring-1 ring-slate-200 dark:ring-white/10" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-black text-blue-900 dark:text-white leading-tight">Serafina</h1>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Sua assistente financeira inteligente</p>
                </div>
              </div>
            </div>

            {/* Messages list */}
            <div className="space-y-6 flex-1 pb-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  data-testid={`message-${message.role}-${index}`}
                  className="w-full"
                >
                  {message.role === 'user' ? (
                    /* User message: Keep exactly as before (right-aligned blue bubble) */
                    <div className="flex justify-end">
                      <div className="max-w-[85%] sm:max-w-[80%] rounded-2xl rounded-tr-sm bg-blue-900 text-white p-3.5 sm:p-4 shadow-sm break-words">
                        {message.images && message.images.length > 0 ? (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {message.images.map((imgSrc, i) => (
                              <img
                                key={i}
                                src={imgSrc}
                                alt={`Comprovante ${i + 1}`}
                                onClick={() => setExpandedImage(imgSrc)}
                                className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-white/20 shadow-sm cursor-pointer hover:opacity-90 hover:scale-[1.02] transition-all"
                                title="Clique para ampliar"
                              />
                            ))}
                          </div>
                        ) : message.image ? (
                          <img
                            src={message.image}
                            alt="Comprovante"
                            onClick={() => setExpandedImage(message.image)}
                            className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-white/20 shadow-sm mb-2 cursor-pointer hover:opacity-90 hover:scale-[1.02] transition-all"
                            title="Clique para ampliar"
                          />
                        ) : null}

                        {message.fileNames && message.fileNames.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {message.fileNames.map((fName, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white dark:bg-slate-700 px-2 py-0.5 rounded-md font-mono">
                                <FiFileText size={12} /> {fName}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
                          {message.content}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Assistant message: Unboxed, full width, avatar + name at top (Image 2 style) */
                    <div className="w-full text-slate-800 dark:text-slate-100">
                      {/* Avatar + Name at top */}
                      <div className="flex items-center gap-2.5 mb-2">
                        <img
                          src={SERAFINA_AVATAR}
                          alt="Serafina"
                          className="w-6 h-6 rounded-full object-cover shadow-sm ring-1 ring-slate-200 dark:ring-white/10"
                        />
                        <span className="font-semibold text-sm text-slate-900 dark:text-white">Serafina</span>
                      </div>

                      {/* Content spanning full width */}
                      <div className="w-full">
                        <MarkdownMessage content={message.content} isUser={false} />

                        {/* Action toolbar on assistant messages */}
                        {(!isStreaming || index !== messages.length - 1) && (
                          <TooltipProvider delayDuration={100}>
                            <div className="mt-3 pt-2 border-t border-slate-200/50 dark:border-white/5 flex items-center gap-1 sm:gap-1.5 text-slate-400">
                              {/* 1. Like */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => handleFeedback(index, 'like')}
                                    className={`p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                                      feedbackMap[index] === 'like'
                                        ? 'text-emerald-500 bg-emerald-500/15 dark:text-emerald-400 dark:bg-emerald-500/20'
                                        : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60'
                                    }`}
                                    aria-label="Boa resposta"
                                  >
                                    <ThumbsUp size={16} className={feedbackMap[index] === 'like' ? 'fill-current' : ''} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  Boa resposta
                                </TooltipContent>
                              </Tooltip>

                              {/* 2. Deslike */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => handleFeedback(index, 'dislike')}
                                    className={`p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                                      feedbackMap[index] === 'dislike'
                                        ? 'text-rose-500 bg-rose-500/15 dark:text-rose-400 dark:bg-rose-500/20'
                                        : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60'
                                    }`}
                                    aria-label="Resposta incorreta"
                                  >
                                    <ThumbsDown size={16} className={feedbackMap[index] === 'dislike' ? 'fill-current' : ''} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  Resposta incorreta
                                </TooltipContent>
                              </Tooltip>

                              {/* 3. Compartilhar */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => handleShareMessage(message.content, index)}
                                    className="p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60"
                                    aria-label="Compartilhar mensagem e prompt"
                                  >
                                    <Share size={16} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  Compartilhar mensagem e prompt
                                </TooltipContent>
                              </Tooltip>

                              {/* 4. Copiar */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyMessage(message.content, index)}
                                    className="p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60"
                                    aria-label="Copiar mensagem"
                                  >
                                    {copiedIndex === index ? (
                                      <Check size={16} className="text-emerald-400" />
                                    ) : (
                                      <Copy size={16} />
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  {copiedIndex === index ? 'Mensagem copiada!' : 'Copiar mensagem'}
                                </TooltipContent>
                              </Tooltip>

                              {/* 5. Som */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => playSpeech(message.content, index)}
                                    disabled={loadingAudioIndex === index}
                                    className={`p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                                      playingAudioIndex === index
                                        ? 'text-cyan-400 bg-cyan-500/20 animate-pulse'
                                        : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60'
                                    }`}
                                    aria-label={playingAudioIndex === index ? 'Pausar voz' : 'Iniciar leitura em voz alta'}
                                  >
                                    {loadingAudioIndex === index ? (
                                      <Loader2 size={16} className="animate-spin text-cyan-400" />
                                    ) : playingAudioIndex === index ? (
                                      <VolumeX size={16} />
                                    ) : (
                                      <Volume2 size={16} />
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  {playingAudioIndex === index ? 'Pausar voz' : 'Iniciar leitura em voz alta'}
                                </TooltipContent>
                              </Tooltip>

                              {/* 6. Gerar Novamente */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => handleRegenerate(index)}
                                    disabled={loading || isStreaming}
                                    className="p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-800/60 disabled:opacity-40"
                                    aria-label="Gerar Novamente"
                                  >
                                    <RotateCw size={15} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={6} className="bg-[#111622] text-white border border-slate-700/80 text-xs px-2.5 py-1 rounded-md shadow-xl font-medium">
                                  Gerar Novamente
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="w-full">
                  <div className="flex items-center gap-2.5 mb-2">
                    <img src={SERAFINA_AVATAR} alt="Serafina" className="w-6 h-6 rounded-full object-cover shadow-sm ring-1 ring-slate-200 dark:ring-white/10" />
                    <span className="font-semibold text-sm text-slate-900 dark:text-white">Serafina</span>
                  </div>
                  <div className="flex gap-1.5 items-center py-2">
                    <div className="w-2 h-2 bg-blue-900 dark:bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-900 dark:bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-900 dark:bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

          </div>
        </div>

        {/* Bottom Floating Pill Bar with ChatGPT-style Fade Gradient */}
        <div 
          className="chat-bottom-bar absolute bottom-0 left-0 right-0 z-10 pointer-events-none flex flex-col justify-end"
          style={{ right: `${scrollbarWidth}px`, '--chat-scrollbar-width': `${scrollbarWidth}px` }}
        >
          {/* Subtle compact fade gradient zone: only appears below the scroll-down button */}
          <div className="w-full h-5 sm:h-6 bg-gradient-to-t from-[#F0F5FA] to-transparent dark:from-[#070D1A] dark:to-transparent pointer-events-none" />

          {/* Solid base container holding the input form with NO horizontal border line */}
          <div className="w-full bg-[#F0F5FA] dark:bg-[#070D1A] pb-2.5 sm:pb-3 pointer-events-auto">
            <div className="max-w-3xl sm:max-w-4xl w-full mx-auto px-4 sm:px-6 relative">
              
              {/* Floating Scroll to Bottom Button - raised ~1mm (-top-12) */}
              {showScrollBottom && (
                <button
                  type="button"
                  onClick={() => scrollToBottom('smooth')}
                  className="absolute -top-12 left-1/2 -translate-x-1/2 z-20 w-8 h-8 rounded-full bg-white dark:bg-[#131D31] border border-slate-200 dark:border-white/15 text-slate-600 dark:text-slate-300 shadow-md hover:shadow-lg hover:scale-105 hover:text-slate-900 dark:hover:text-white transition-all flex items-center justify-center cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-200 pointer-events-auto"
                  title="Rolar para o final"
                  aria-label="Rolar para o final"
                >
                  <ArrowDown size={15} />
                </button>
              )}

            {/* Pending attachments preview (images + documents) */}
            {(pendingImages.length > 0 || pendingFiles.length > 0) && (
              <div data-testid="pending-attachments-preview" className="mb-2 p-2 bg-slate-50 dark:bg-slate-800/90 border border-blue-900/10 dark:border-white/10 rounded-xl flex-shrink-0 transition-all">
                <div className="flex items-center justify-between mb-1.5 px-1">
                  <span className="text-[11px] font-medium text-slate-600 dark:text-cyan-300">
                    {pendingImages.length + pendingFiles.length} anexo(s) pronto(s) para análise
                  </span>
                  <button
                    type="button"
                    onClick={() => { setPendingImages([]); setPendingFiles([]); }}
                    className="text-[11px] text-red-500 hover:text-red-700 dark:text-red-400 transition-colors cursor-pointer"
                  >
                    Remover todos
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-0.5">
                  {/* Images */}
                  {pendingImages.map((img) => (
                    <div
                      key={img.id}
                      className="relative group w-11 h-11 rounded-lg overflow-hidden border border-slate-200 dark:border-white/20 bg-slate-100 dark:bg-slate-900 flex-shrink-0 shadow-sm cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                      onClick={() => setExpandedImage(img.base64)}
                      title="Clique para ampliar"
                    >
                      <img src={img.base64} alt={img.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-image-${img.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingImages(prev => prev.filter(item => item.id !== img.id));
                        }}
                        className="absolute top-0.5 right-0.5 bg-red-600/90 hover:bg-red-600 text-white rounded-full p-0.5 transition-all opacity-80 group-hover:opacity-100 shadow"
                        title="Remover"
                      >
                        <FiX size={10} />
                      </button>
                    </div>
                  ))}

                  {/* Documents / Spreadsheets */}
                  {pendingFiles.map((f) => (
                    <div
                      key={f.id}
                      className="relative group flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-white/20 bg-white dark:bg-slate-900 flex-shrink-0 shadow-sm"
                    >
                      <div className="w-6 h-6 rounded bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                        <FiFileText size={14} />
                      </div>
                      <div className="max-w-[130px] min-w-0 pr-4">
                        <div className="text-xs font-medium text-slate-800 dark:text-white truncate">{f.name}</div>
                        <div className="text-[10px] text-slate-400">{formatFileSize(f.size)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingFiles(prev => prev.filter(item => item.id !== f.id))}
                        className="absolute top-1 right-1 text-slate-400 hover:text-red-500 p-0.5 transition-colors cursor-pointer"
                        title="Remover arquivo"
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ChatGPT Style Rounded Pill Input Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="relative flex items-center w-full flex-shrink-0">
              {/* Hidden file pickers */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => { addFilesToList(e.target.files); e.target.value = ''; }}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.txt,.json"
                multiple
                onChange={(e) => { addFilesToList(e.target.files); e.target.value = ''; }}
                className="hidden"
              />

              {/* Pill Capsule Container */}
              <div className="relative flex items-center w-full bg-white dark:bg-[#0B1528] border border-slate-300 dark:border-white/15 rounded-full shadow-sm hover:border-slate-400 dark:hover:border-white/25 focus-within:border-blue-900 dark:focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-blue-900/15 dark:focus-within:ring-cyan-500/20 transition-all px-2.5 py-1.5 min-h-[52px]">
                
                {/* Extreme Left Inside: '+' Button with Popover Menu */}
                <div className="relative flex items-center flex-shrink-0" ref={attachMenuRef}>
                  <button
                    type="button"
                    data-testid="chat-plus-button"
                    disabled={loading || isStreaming}
                    onClick={() => setShowAttachMenu(prev => !prev)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      showAttachMenu
                        ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rotate-45'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                    title="Anexar imagem ou arquivo"
                  >
                    <FiPlus size={20} className="transition-transform duration-200" />
                  </button>

                  {/* Popover Menu above the '+' button */}
                  {showAttachMenu && (
                    <div className="absolute left-0 bottom-12 z-30 bg-white dark:bg-[#0E1B33] border border-slate-200 dark:border-white/15 rounded-2xl shadow-xl p-1.5 min-w-[210px] animate-in fade-in slide-in-from-bottom-2 duration-150">
                      <button
                        type="button"
                        data-testid="attach-image-option"
                        onClick={() => { setShowAttachMenu(false); imageInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-xl transition-colors text-left cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-900 dark:text-cyan-400 flex items-center justify-center flex-shrink-0">
                          <FiImage size={15} />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">Imagem</div>
                          <div className="text-[10px] text-slate-400">Comprovantes, fotos</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        data-testid="attach-file-option"
                        onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-xl transition-colors text-left cursor-pointer mt-0.5"
                      >
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                          <FiFileText size={15} />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">Arquivo / Tabela</div>
                          <div className="text-[10px] text-slate-400">Excel, CSV, PDF, TXT</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {/* Center Inside: Text Input Area */}
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onPaste={handlePasteEvent}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder={
                    isRecording
                      ? 'Ouvindo sua voz... Fale com a Serafina'
                      : isTranscribing
                      ? 'Transcrevendo seu áudio...'
                      : (pendingImages.length > 0 || pendingFiles.length > 0)
                      ? 'Escreva uma mensagem sobre os anexos ou envie...'
                      : 'Pergunte à Serafina...'
                  }
                  disabled={loading || isStreaming || isTranscribing}
                  rows={1}
                  className="chat-input flex-1 !bg-transparent dark:!bg-transparent px-3 py-1.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none resize-none max-h-24 overflow-y-auto !border-none focus:ring-0"
                  style={{ backgroundColor: 'transparent' }}
                />

                {/* Right Inside: Discreet Grey Microphone Button for Dictation */}
                <button
                  type="button"
                  data-testid="chat-mic-button"
                  onClick={toggleRecording}
                  disabled={loading || isStreaming || isTranscribing}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all flex-shrink-0 mr-1.5 cursor-pointer ${
                    isRecording
                      ? 'bg-red-500 text-white animate-pulse shadow-md ring-2 ring-red-400/40'
                      : isTranscribing
                      ? 'text-blue-900 dark:text-cyan-400 animate-spin'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                  }`}
                  title={isRecording ? 'Parar ditado' : isTranscribing ? 'Transcrevendo...' : 'Ditar texto no campo'}
                >
                  {isRecording ? <FiMicOff size={18} /> : <FiMic size={18} />}
                </button>

                {/* Extreme Right Inside: Dynamic Action Button (Stop | Voice Mode Waves | Send) */}
                {(loading || isStreaming) ? (
                  <button
                    data-testid="chat-stop-button"
                    type="button"
                    onClick={handleStop}
                    className="w-9 h-9 rounded-full bg-blue-900 hover:bg-blue-800 text-white flex items-center justify-center transition-all flex-shrink-0 shadow-sm cursor-pointer"
                    title="Interromper resposta"
                  >
                    <FiSquare size={14} className="fill-current" />
                  </button>
                ) : !input.trim() && pendingImages.length === 0 && pendingFiles.length === 0 ? (
                  <button
                    data-testid="chat-voice-mode-button"
                    type="button"
                    onClick={() => {
                      stopSpeech();
                      if (isRecording) stopRecording();
                      setIsVoiceModeActive(true);
                    }}
                    className="w-9 h-9 rounded-full bg-blue-900 hover:bg-blue-800 text-white flex items-center justify-center transition-all flex-shrink-0 shadow-sm cursor-pointer group"
                    title="Iniciar conversa por voz com a Serafina"
                  >
                    <RiVoiceprintFill size={19} className="group-hover:scale-110 transition-transform" />
                  </button>
                ) : (
                  <button
                    data-testid="chat-send-button"
                    type="submit"
                    className="w-9 h-9 rounded-full bg-blue-900 hover:bg-blue-800 text-white flex items-center justify-center transition-all flex-shrink-0 shadow-sm cursor-pointer"
                    title="Enviar mensagem"
                  >
                    <FiSend size={15} className="translate-x-[1px]" />
                  </button>
                )}
              </div>
            </form>

            {/* Discreet bottom caption */}
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 text-center flex-shrink-0">
              Cole imagens ou arquivos com Ctrl+V. A Serafina pode analisar comprovantes, tabelas Excel e falar com você.
            </p>
          </div>
        </div>
      </div>
    </main>

      {/* Lightbox Modal (Zoom view) */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setExpandedImage(null)}
        >
          <div
            className="relative max-w-[94vw] max-h-[90vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setExpandedImage(null)}
              className="absolute -top-11 right-0 sm:-right-4 text-white/90 hover:text-white bg-white/10 hover:bg-white/25 p-2 rounded-full transition-all cursor-pointer shadow-lg"
              title="Fechar (Esc)"
            >
              <FiX size={22} />
            </button>
            <img
              src={expandedImage}
              alt="Comprovante ampliado"
              className="max-w-full max-h-[85vh] rounded-xl object-contain shadow-2xl border border-white/10"
            />
          </div>
        </div>
      )}
      {/* Immersive Continuous Voice Mode Modal */}
      {isVoiceModeActive && (
        <SerafinaVoiceMode
          messages={messages}
          setMessages={setMessages}
          onClose={() => {
            stopSpeech();
            setIsVoiceModeActive(false);
          }}
          API={API}
        />
      )}
    </div>
  );
};
